// Presentation helpers for the LockPlan analysis, shared by the panel and the
// editor's inline annotations so both describe a `txn` the same way.
//
// The two decisions the analysis surfaces:
//   - per-`txn` *strategy* — a statically-known footprint (pessimistic 2PL /
//     "lock") vs. a data-dependent one (optimistic OCC / validate-and-retry);
//   - whole-program *memory mode* — in-place mutable-under-lock vs. versioned
//     MVCC, and, when it fell back to MVCC, why in-place wasn't possible.

import type { LockPlan, TxnStrategy } from "../src/compiler.js";

/** A tiny glyph coding the strategy: 🔒 pessimistic lock, ⚡ optimistic OCC. */
export function strategyBadge(s: TxnStrategy): string {
  return s.kind === "lock" ? "🔒" : "⚡";
}

/** One-line label, e.g. `locks {from, to}` or `data-dependent → OCC`. */
export function strategyLabel(s: TxnStrategy): string {
  return s.kind === "lock"
    ? `static footprint {${s.footprint.join(", ")}}`
    : "data-dependent → OCC";
}

/** `lp-lock` / `lp-occ` — the CSS colour class for chips and inline text. */
export function strategyClass(s: TxnStrategy): string {
  return s.kind === "lock" ? "lp-lock" : "lp-occ";
}

/** Markdown explaining a strategy, for the editor hover / panel tooltip. */
export function strategyTooltip(s: TxnStrategy): string {
  if (s.kind === "lock") {
    const fp = s.footprint.length ? `\`{${s.footprint.join(", ")}}\`` : "∅";
    return (
      `**🔒 Pessimistic (2PL)** — this \`txn\`'s footprint is statically known ` +
      `(${fp}), so the compiler can lock exactly those root variables, run the ` +
      `body once, and unlock. No snapshot, validation, or retry.`
    );
  }
  return (
    "**⚡ Optimistic (OCC)** — this `txn`'s footprint is *data-dependent*: which " +
    "objects it touches can't be enumerated at compile time (a set iteration, a " +
    "call, a nested-object access…). It runs speculatively — snapshot, buffer " +
    "writes, validate the read set at commit, and retry on conflict."
  );
}

export interface ModeSummary {
  /** "in-place" | "mvcc". */
  mode: LockPlan["mode"];
  label: string;
  detail: string;
  /** `lp-inplace` / `lp-mvcc`, for the badge colour. */
  cls: string;
}

/** A human-readable summary of the whole-program memory mode decision. */
export function modeSummary(plan: LockPlan): ModeSummary {
  if (plan.mode === "in-place") {
    return {
      mode: "in-place",
      label: "in-place locks",
      detail:
        "Every txn has a statically-known footprint and no object is touched " +
        "outside a transaction, so heap objects can be plain mutable records " +
        "under a per-object lock — no versioning. Identical to hand-written locking.",
      cls: "lp-inplace",
    };
  }
  const why = plan.inPlaceBlockedBy ? ` In-place not possible: ${plan.inPlaceBlockedBy}.` : "";
  return {
    mode: "mvcc",
    label: "MVCC",
    detail:
      "Heap objects are versioned records read against a global clock, so " +
      "optimistic transactions get a consistent lock-free snapshot." +
      why,
    cls: "lp-mvcc",
  };
}
